<template>
  <div v-if="fetchingEntriSetup" class="shimmer animate-shimmer" />
  <div v-else-if="showMailConnectDomainBanner" class="mail-connect-domain-banner">
    <div class="banner-content">
      <p v-html="$t('bannerText.domainActivate.strongText', [domainName, brandName])" />
      <div class="actions">
        <span v-if="entriSetupPossible" @click="emitAutomaticSetup(setupMode.EMAIL)">
          {{ $t("bannerText.domainActivate.connectAutomatically") }}
        </span>
        <span @click="emitManualSetup(setupMode.EMAIL)">
          {{
            entriSetupPossible
              ? $t("bannerText.domainActivate.connectManually")
              : $t("bannerText.domainActivate.btnText")
          }}
        </span>
      </div>
    </div>
  </div>
  <div v-else-if="showSiteConnectBanner" class="site-connect-domain-banner">
    <div class="banner-content">
      <p>{{ $t("bannerText.siteActivate.strongText") }}</p>
      <div class="actions">
        <span v-if="entriSetupPossible" @click="emitAutomaticSetup(setupMode.SITE)">
          {{ $t("bannerText.siteActivate.connectDomainToSite") }}
        </span>
        <span @click="emitManualSetup(setupMode.SITE)">
          {{
            entriSetupPossible
              ? $t("bannerText.domainActivate.connectManually")
              : $t("bannerText.domainActivate.btnText")
          }}
        </span>
      </div>
    </div>
  </div>
</template>
<script>
import { getCurrentUserEmail, hasMailSuiteProduct, hasSiteProduct, toCapitalise } from "@/helpers";
import brand from "@/helpers/brand";
import { ENTRI_SETUP_MODE, ORDER, SITE_ORDER } from "@/helpers/const";
import { ENTRI_SETUP_TYPE } from "@/helpers/entriConfig";
import eventBus from "@/helpers/event-bus";
import { MEDUSA_EVENTS_MAP } from "@/telemetry/events";
import { sendOrderAndCustomerEvent } from "@/telemetry/medusaUtils";
import orderEvents from "@/telemetry/orderEvents";
import { mapGetters } from "vuex";

export default {
  name: "EntriBanner",
  data() {
    return {
      setupMode: ENTRI_SETUP_MODE,
      brandName: toCapitalise(brand.getBrandName()),
    };
  },
  computed: {
    ...mapGetters(["products"]),
    ...mapGetters({
      domainName: "domain/domainName",
      domainConnectData: "domain/domainConnectData",
      isDomainVerifiedAndConnected: "domain/domainVerifiedAndConnected",
      isSiteVerifiedAndConnected: "domain/siteVerifiedAndConnected",
      entriSetupPossible: "entri/setupPossible",
      fetchingEntriSetup: "entri/isFetching",
    }),
    showMailConnectDomainBanner() {
      return hasMailSuiteProduct(this.products) && !this.isDomainVerifiedAndConnected;
    },
    showSiteConnectBanner() {
      return hasSiteProduct(this.products) && !this.isSiteVerifiedAndConnected;
    },
  },
  methods: {
    emitAutomaticSetup(product) {
      eventBus.$emit("handleEntriSetupRedirection", ENTRI_SETUP_TYPE.AUTOMATIC, product);
      this.sendDomainBannerClicked(ENTRI_SETUP_TYPE.AUTOMATIC, product);
    },
    emitManualSetup(product) {
      eventBus.$emit("handleEntriSetupRedirection", ENTRI_SETUP_TYPE.MANUAL, product);
      this.sendDomainBannerClicked(ENTRI_SETUP_TYPE.MANUAL, product);
      this.$router.push({
        path: product === ENTRI_SETUP_MODE.EMAIL ? "/activate" : "/connect-site-domain",
      });
    },
    getDnsAndEmailMedusaAttrs() {
      const records = this.domainConnectData
        ? {
            mx: (this.domainConnectData.mxRecords || []).join(" , "),
            spf: (this.domainConnectData.spfRecords || []).join(" , "),
            ns: (this.domainConnectData.nsRecords || []).join(" , "),
            a: (this.domainConnectData.aRecords || []).join(" , "),
            domainconnect: this.domainConnectData.isDomainConnectDomain,
          }
        : {};

      return {
        adminEmail: getCurrentUserEmail(),
        ...records,
      };
    },
    sendDomainBannerClicked(setupType, setupFor) {
      const setupForMail = setupFor === ENTRI_SETUP_MODE.EMAIL;
      const commonMedusaAttribute = {
        auto_domain_setup_supported: this.entriSetupPossible,
        connection_method: setupType,
      };

      const medusaPayload = {
        ...this.getDnsAndEmailMedusaAttrs(),
        ...commonMedusaAttribute,
        dns_setup_for: setupFor,
      };

      sendOrderAndCustomerEvent.call(this, {
        eventName: setupForMail
          ? MEDUSA_EVENTS_MAP.MAIL_CONNECT_DOMAIN_BANNER_CLICKED
          : MEDUSA_EVENTS_MAP.SITE_CONNECT_DOMAIN_BANNER_CLICKED,
        ...medusaPayload,
        /* Must follow the event above: the ORDER default from `getCommonMedusaParams` would key the
           site variant to the mail order instead of the site order. */
        user_type: setupForMail ? ORDER : SITE_ORDER,
      });

      if (setupForMail) {
        orderEvents.logMailConnectDomainBannerClicked(medusaPayload);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.mail-connect-domain-banner,
.site-connect-domain-banner {
  border-radius: 4px;
  background: #ffdcdb;
  display: flex;
  padding: 16px 12px;
  align-items: flex-start;
  gap: 4px;

  .banner-content {
    padding-left: 30px;
    background-image: url("../../../assets/warning-icon-red.svg");
    background-repeat: no-repeat;
    background-position: top 3px left 0px;
  }

  p {
    color: #333;
    font-size: 13px;
    font-weight: 400;
    line-height: 150%;
    margin-bottom: 20px;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 20px;

    span {
      color: var(--linkBtnCol);
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      text-transform: uppercase;
      cursor: pointer;
    }
  }
}

div.shimmer {
  width: 100%;
  height: 60px;
  border-radius: 5px;
}
</style>
